/**
 * Swift's `Double.description`: shortest round-trip digits (as JavaScript also produces),
 * decimal notation with a mandatory fraction for 1e-4 ≤ |x| < 1e16, otherwise exponent
 * notation with a signed, at-least-two-digit exponent ("1e-05", "1.5e+16").
 */
export function describeDouble(value: number): string {
  if (Number.isNaN(value)) return 'nan';
  if (!Number.isFinite(value)) return value > 0 ? 'inf' : '-inf';
  if (value === 0) return Object.is(value, -0) ? '-0.0' : '0.0';

  const sign = value < 0 ? '-' : '';
  const [mantissa = '', exponentText = '0'] = Math.abs(value).toExponential().split('e');
  const digits = mantissa.replace('.', '');
  const exponent = Number(exponentText);

  if (exponent < -4 || exponent >= 16) {
    const fraction = digits.length > 1 ? `.${digits.slice(1)}` : '';
    const magnitude = String(Math.abs(exponent)).padStart(2, '0');
    return `${sign}${digits[0]}${fraction}e${exponent < 0 ? '-' : '+'}${magnitude}`;
  }
  if (exponent < 0) return `${sign}0.${'0'.repeat(-exponent - 1)}${digits}`;
  const whole = digits.slice(0, exponent + 1).padEnd(exponent + 1, '0');
  const fraction = digits.slice(exponent + 1) || '0';
  return `${sign}${whole}.${fraction}`;
}

/** The contract's encoding of a Double: `describeDouble`, with NaN and infinities spelled out. */
export function encodeDouble(value: number): string {
  if (Number.isNaN(value)) return 'NaN';
  if (!Number.isFinite(value)) return value > 0 ? 'Infinity' : '-Infinity';
  return describeDouble(value);
}
